import React, { useState } from 'react';
import { Modal, TextField, Switch, CircularProgress } from '@mui/material';
import { X, Info, AlertTriangle } from 'lucide-react';
import { useCommercialPlan } from 'contexts';
import { ICreateCommercialPlanInputModel } from 'inputModels';
import { CurrencyInput } from 'components';
import {
  ModalWrapper,
  ModalHeader,
  ModalTitleContainer,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  CloseButton,
  FormGroup,
  FormSection,
  FormSectionTitle,
  FormGrid,
  SwitchContainer,
  SwitchItem,
  SwitchLabel,
  InfoAlert,
  WarningAlert,
  ErrorAlert,
  LoadingContainer,
  LoadingText,
} from './modalStyles';

interface CreatePlanModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreatePlanModal: React.FC<CreatePlanModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { handleCreatePlan } = useCommercialPlan();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<ICreateCommercialPlanInputModel>({
    name: '',
    description: '',
    isActive: true,
    isDefault: false,
    minimumConsumption: 0,
    maxPublicFonts: 0,
    extraFontsTaxRate: 0,
  });

  const handleInputChange = (
    field: keyof ICreateCommercialPlanInputModel,
    value: any
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    if (formData.minimumConsumption < 0) {
      setError('Consumo mínimo deve ser maior ou igual a zero');
      return;
    }

    if (formData.maxPublicFonts < 0) {
      setError('Quantidade máxima de fontes deve ser maior ou igual a zero');
      return;
    }

    if (formData.extraFontsTaxRate < 0 || formData.extraFontsTaxRate > 100) {
      setError('Taxa de fontes extras deve estar entre 0 e 100');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const success = await handleCreatePlan(formData);
      if (success) {
        onSuccess();
        setFormData({
          name: '',
          description: '',
          isActive: true,
          isDefault: false,
          minimumConsumption: 0,
          maxPublicFonts: 0,
          extraFontsTaxRate: 0,
        });
      } else {
        setError('Erro ao criar plano');
      }
    } catch (err) {
      setError('Erro ao criar plano');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        description: '',
        isActive: true,
        isDefault: false,
        minimumConsumption: 0,
        maxPublicFonts: 0,
        extraFontsTaxRate: 0,
      });
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableScrollLock={false}
      keepMounted={false}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <ModalWrapper>
        <ModalHeader>
          <ModalTitleContainer>
            <ModalTitle id="modal-title">Criar Novo Plano Comercial</ModalTitle>
            <ModalDescription id="modal-description">
              Preencha as informações para criar um novo plano comercial
            </ModalDescription>
          </ModalTitleContainer>
          <CloseButton onClick={handleClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          {error && (
            <ErrorAlert>
              <AlertTriangle size={16} />
              {error}
            </ErrorAlert>
          )}

          <FormSection>
            <FormSectionTitle>Informações Básicas</FormSectionTitle>

            <FormGroup>
              <TextField
                label="Nome do Plano"
                value={formData.name}
                onChange={e => handleInputChange('name', e.target.value)}
                fullWidth
                required
                disabled={loading}
                size="small"
              />
            </FormGroup>

            <FormGroup>
              <TextField
                label="Descrição"
                value={formData.description}
                onChange={e => handleInputChange('description', e.target.value)}
                fullWidth
                multiline
                rows={3}
                disabled={loading}
                size="small"
                placeholder="Descreva o propósito e características do plano"
                slotProps={{
                  htmlInput: {
                    maxLength: 400,
                  },
                }}
              />
            </FormGroup>
          </FormSection>

          <FormSection>
            <FormSectionTitle>Configurações de Consumo</FormSectionTitle>

            <FormGrid>
              <FormGroup>
                <CurrencyInput
                  label="Consumo Mínimo (R$)"
                  value={formData.minimumConsumption}
                  onChange={(value) => handleInputChange('minimumConsumption', value)}
                  disabled={loading}
                  helperText="Valor mínimo em reais"
                />
              </FormGroup>

              <FormGroup>
                <TextField
                  label="Máx. Fontes Públicas"
                  type="text"
                  value={formData.maxPublicFonts}
                  onChange={e =>
                    handleInputChange(
                      'maxPublicFonts',
                      parseInt(e.target.value || '0')
                    )
                  }
                  fullWidth
                  disabled={loading}
                  inputProps={{ min: 0 }}
                  size="small"
                  inputMode="decimal"
                  helperText="Limite de fontes públicas"
                />
              </FormGroup>
            </FormGrid>

            <FormGroup>
              <TextField
                label="Taxa de Fontes Extras (%)"
                type="text"
                value={formData.extraFontsTaxRate}
                onChange={e =>
                  handleInputChange(
                    'extraFontsTaxRate',
                    parseFloat(e.target.value || '0')
                  )
                }
                fullWidth
                disabled={loading}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                size="small"
                inputMode="decimal"
                helperText="Taxa aplicada quando exceder o limite de fontes"
              />
            </FormGroup>
          </FormSection>

          <FormSection>
            <FormSectionTitle>Status do Plano</FormSectionTitle>
            {formData.isDefault && (
              <InfoAlert>
                <Info size={16} />
                Este plano será definido como padrão para novos usuários
              </InfoAlert>
            )}
            <SwitchContainer>
              <SwitchItem>
                <Switch
                  checked={formData.isActive}
                  onChange={e =>
                    handleInputChange('isActive', e.target.checked)
                  }
                  disabled={loading}
                  size="small"
                />
                <SwitchLabel>Plano Ativo</SwitchLabel>
              </SwitchItem>

              <SwitchItem>
                <Switch
                  checked={formData.isDefault}
                  onChange={e =>
                    handleInputChange('isDefault', e.target.checked)
                  }
                  disabled={loading}
                  size="small"
                />
                <SwitchLabel>Plano Padrão</SwitchLabel>
              </SwitchItem>
            </SwitchContainer>

            {formData.extraFontsTaxRate > 0 && (
              <WarningAlert>
                <AlertTriangle size={16} />
                Taxa de {formData.extraFontsTaxRate}% será aplicada quando
                exceder o limite de {formData.maxPublicFonts} fontes
              </WarningAlert>
            )}
          </FormSection>
        </ModalContent>

        <ModalFooter>
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              background: 'white',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !formData.name.trim()}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              background: '#293778',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              opacity: loading || !formData.name.trim() ? 0.5 : 1,
            }}
          >
            {loading ? (
              <LoadingContainer>
                <CircularProgress size={16} />
                <LoadingText>Criando...</LoadingText>
              </LoadingContainer>
            ) : (
              'Criar Plano'
            )}
          </button>
        </ModalFooter>
      </ModalWrapper>
    </Modal>
  );
};

export default CreatePlanModal;
